"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="29010" className="flex gap-6">
        {[1, 2, 3, 4].map((i, idx) => <div key={i} data-index={idx} className="h-5 w-16 animate-pulse rounded bg-white/5" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.filter(item => item.label !== 'Jet Sales and Acquisitions2' && item.label !== 'Contact').map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-[13px] font-semibold tracking-widest uppercase text-white/80 transition-all duration-300 hover:text-primary after:absolute after:bottom-[-4px] after:left-1/2 after:h-[1px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3].map((i, idx) => <div key={i} data-index={idx} className="h-8 w-full animate-pulse rounded bg-white/5" />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.filter(item => item.label !== 'Jet Sales and Acquisitions2' && item.label !== 'Contact').map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-semibold tracking-wider uppercase text-white/90 transition-colors hover:text-primary" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="pt-4 flex flex-col gap-3">
        <Button asChild variant="default" className="w-full font-bold tracking-widest uppercase shadow-lg" onClick={onClose}>
          <Link to="#">
            Request a Flight
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("dark fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500", isScrolled ? "bg-background/80 shadow-2xl backdrop-blur-lg border-b border-border/50" : "bg-background border-b border-border/20")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 outline-none">
          <div className="relative flex items-center justify-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618318/img/starboard_aviation_group_logo_refined.png" alt="Starboard Aviation Group" className="h-7 w-auto object-contain brightness-0 invert" />
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="44" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Button asChild variant="default" className="font-bold tracking-widest uppercase shadow-md transition-all duration-300 hover:shadow-xl border-none px-8">
            <Link to="#">Request a Flight</Link>
          </Button>
        </div>

        <span className="md:hidden flex items-center">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/5" aria-label="Toggle menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0 bg-background/95 backdrop-blur-2xl border-l border-border/50">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation Menu</SheetTitle>
              </SheetHeader>
              <div className="pt-20">
                <MenuProvider menu_id="44" className="mobile-menu">
                  <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
                </MenuProvider>
              </div>
            </SheetContent>
          </Sheet>
        </span>
      </div>
    </header>;
}